import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';

import { PageHeader } from '@opengov/components-page-header';
import { Eye, Magnify } from '@opengov/react-capital-assets';

import { SectionCard } from '../components/vcm/SectionCard';
import { TierBadge } from '../components/vcm/TierBadge';
import { useVcm } from '../contexts/VcmContext';
import { useAppNavigation } from '../hooks/useAppNavigation';
import { TEXAS_COUNTIES } from '../lib/vcm/counties';
/* MOCK DATA — replace with real /v2 API calls when backend is connected */
import { matchIstatToClients, compareIstatToCase } from '../lib/vcm/istat-matching';
import { mockGetAllAudit, mockGetAllFollowUps, mockGetAllGoals, mockGetAllReferrals, mockGetCases, mockGetClients, mockGetCallLog, mockGetWorkOrdersByCaseId } from '../mocks/vcm-mock-data';
import type { TcrCase, IstatMatchResult, IstatDiscrepancy } from '../types/vcm';

const ADMIN_TABS = [
  { id: 'assignment', label: 'Case Assignment' },
  { id: 'caseload', label: 'Caseload Manager' },
  { id: 'events', label: 'Event Management' },
  { id: 'rejected', label: 'Rejected Cases' },
  { id: 'closed', label: 'Closed Cases' },
  { id: 'audit', label: 'Audit Viewer' },
  { id: 'calls', label: 'Call Center' },
] as const;
type AdminTabId = (typeof ADMIN_TABS)[number]['id'];

const REOPEN_ROLES = ['Organization Manager', 'Supervisor', 'Program Manager'];
const CASEWORKERS = ['Noah Golding', 'Marcus Lee', 'Darius Cole'];

interface IstatRecord {
  incidentId: string;
  incidentDate: string;
  fieldteamMember: string;
  propertyType: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  county: string;
  state: string;
  zip: string;
  typeOfStructure: string;
  residenceType: string;
  ownerOrRenter: string;
  insuranceStatus: string;
  insuranceCoverage: string;
  incidentType: string;
  waterDepth: string;
  utilityOutages: string;
  utilityDamages: string;
  survivorDamageLevel: string;
  generalComments: string;
  regionDamageLevel: string;
  regionStatus: string;
  regionComments: string;
  latitude: string;
  longitude: string;
  createdDate: string;
  potentialDup: string;
  parcelId: string | null;
}

interface VerificationItem {
  id: string;
  clientName: string;
  address: string;
  county: string;
  damageLevel: string;
  incidentType: string;
  dateReceived: string;
  source: 'iSTAT' | 'Manual';
  istatRecord?: IstatRecord;
}

interface AssignmentItem {
  id: string;
  clientName: string;
  caseId: string;
  county: string;
  tier: string;
  assignedTo: string | null;
}

interface RejectedItem extends VerificationItem {
  reason: string;
  rejectedAt: string;
  rejectedBy: string;
}

interface ManagedEvent {
  name: string;
  programCode: string;
  startDate: string;
  counties: string[];
  status: 'Active' | 'Closed';
}

/* MOCK DATA */
const INITIAL_VERIFICATION: VerificationItem[] = [
  {
    id: 'ver-1',
    clientName: 'Maria Gonzalez',
    address: '1205 Cedar Ln, Austin, TX 78702',
    county: 'Travis',
    damageLevel: 'Major',
    incidentType: 'Initial Intake',
    dateReceived: '2026-05-28',
    source: 'iSTAT',
    istatRecord: {
      incidentId: 'INC-2026-4879-0341', incidentDate: '2026-05-12', fieldteamMember: 'R. Johnson',
      propertyType: 'Residential', firstName: 'Maria', lastName: 'Gonzalez',
      phone: '512-555-0399', email: 'mgonzalez@email.com',
      address: '1205 Cedar Ln', city: 'Austin', county: 'Travis', state: 'TX', zip: '78702',
      typeOfStructure: 'Single Family', residenceType: 'Primary', ownerOrRenter: 'Renter',
      insuranceStatus: 'No', insuranceCoverage: 'None', incidentType: 'Initial Intake',
      waterDepth: '', utilityOutages: '', utilityDamages: '',
      survivorDamageLevel: 'Major', generalComments: 'Veteran presented with housing instability. Currently staying with family. Requests SSVF assistance.',
      regionDamageLevel: 'Major', regionStatus: 'Verified', regionComments: 'Confirmed by field team on 5/14.',
      latitude: '30.2672', longitude: '-97.7431', createdDate: '2026-05-14T09:30:00Z', potentialDup: 'No', parcelId: 'TR-2024-12050',
    },
  },
  {
    id: 'ver-2',
    clientName: 'James Whitfield',
    address: '834 Oakwood Dr, Bastrop, TX 78602',
    county: 'Bastrop',
    damageLevel: 'Destroyed',
    incidentType: 'Housing Assessment',
    dateReceived: '2026-05-29',
    source: 'iSTAT',
    istatRecord: {
      incidentId: 'INC-2026-4879-0387', incidentDate: '2026-05-12', fieldteamMember: 'S. Martinez',
      propertyType: 'Residential', firstName: 'James', lastName: 'Whitfield',
      phone: '979-555-0412', email: 'jwhitfield@email.com',
      address: '834 Oakwood Dr', city: 'Bastrop', county: 'Bastrop', state: 'TX', zip: '78602',
      typeOfStructure: 'Manufactured Home', residenceType: 'Primary', ownerOrRenter: 'Owner',
      insuranceStatus: 'Yes', insuranceCoverage: 'Homeowners', incidentType: 'Housing Assessment',
      waterDepth: '', utilityOutages: '', utilityDamages: '',
      survivorDamageLevel: 'Destroyed', generalComments: 'Veteran at risk of homelessness. Lease terminating in 30 days. No family support available. Requesting emergency housing placement and benefits navigation.',
      regionDamageLevel: 'Destroyed', regionStatus: 'Verified', regionComments: 'Total loss confirmed. Structure condemned.',
      latitude: '30.1105', longitude: '-97.3286', createdDate: '2026-05-15T11:00:00Z', potentialDup: 'No', parcelId: 'BA-2024-08341',
    },
  },
  {
    id: 'ver-3',
    clientName: 'Linh Tran',
    address: '410 Mesquite Ave, San Marcos, TX 78666',
    county: 'Hays',
    damageLevel: 'Minor',
    incidentType: 'Benefits Review',
    dateReceived: '2026-05-30',
    source: 'iSTAT',
    istatRecord: {
      incidentId: 'INC-2026-4879-0402', incidentDate: '2026-05-13', fieldteamMember: 'K. Williams',
      propertyType: 'Residential', firstName: 'Linh', lastName: 'Tran',
      phone: '512-555-0488', email: 'ltran@email.com',
      address: '410 Mesquite Ave', city: 'San Marcos', county: 'Hays', state: 'TX', zip: '78666',
      typeOfStructure: 'Apartment', residenceType: 'Primary', ownerOrRenter: 'Renter',
      insuranceStatus: 'Yes', insuranceCoverage: 'Renters', incidentType: 'Benefits Review',
      waterDepth: '', utilityOutages: '', utilityDamages: '',
      survivorDamageLevel: 'Minor', generalComments: 'Veteran requesting benefits counseling. Has pending VA claim. Needs assistance with documentation and appeals process.',
      regionDamageLevel: 'Minor', regionStatus: 'Verified', regionComments: 'Minor damage confirmed.',
      latitude: '29.8833', longitude: '-97.9414', createdDate: '2026-05-16T14:00:00Z', potentialDup: 'No', parcelId: 'HY-2024-04101',
    },
  },
  {
    id: 'ver-4',
    clientName: 'Elena Martinez',
    address: '407 Willow Bend Dr, Georgetown, TX 78626',
    county: 'Williamson',
    damageLevel: 'Destroyed',
    incidentType: 'Housing Assessment',
    dateReceived: '2026-06-10',
    source: 'iSTAT',
    istatRecord: {
      incidentId: 'INC-2026-4879-0510',
      incidentDate: '2026-06-08',
      fieldteamMember: 'T. Reeves',
      propertyType: 'Residential',
      firstName: 'Elena',
      lastName: 'Martinez',
      phone: '512-555-0134',
      email: 'elena.martinez@example.org',
      address: '407 Willow Bend Dr',
      city: 'Georgetown',
      county: 'Williamson',
      state: 'TX',
      zip: '78626',
      typeOfStructure: 'Single Family',
      residenceType: 'Primary',
      ownerOrRenter: 'Owner',
      insuranceStatus: 'Yes',
      insuranceCoverage: 'Homeowners',
      incidentType: 'Housing Assessment',
      waterDepth: '',
      utilityOutages: '',
      utilityDamages: '',
      survivorDamageLevel: 'Destroyed',
      generalComments: 'Veteran facing eviction. Landlord has issued 30-day notice. VA claim approved but housing voucher not yet issued. Needs emergency housing referral.',
      regionDamageLevel: 'Destroyed',
      regionStatus: 'Verified',
      regionComments: 'Re-assessed after follow-up. Priority escalated.',
      latitude: '30.6324',
      longitude: '-97.6770',
      createdDate: '2026-06-10T10:00:00Z',
      potentialDup: 'Yes',
      parcelId: 'WM-2024-00407',
    },
  },
  {
    id: 'ver-5',
    clientName: 'Robert Jackson',
    address: '18 County Road 419, Bastrop, TX 78602',
    county: 'Bastrop',
    damageLevel: 'Major',
    incidentType: 'Benefits Review',
    dateReceived: '2026-06-11',
    source: 'iSTAT',
    istatRecord: {
      incidentId: 'INC-2026-4879-0528',
      incidentDate: '2026-06-09',
      fieldteamMember: 'R. Johnson',
      propertyType: 'Residential',
      firstName: 'Rob',
      lastName: 'Jackson',
      phone: '512-555-9999',
      email: '',
      address: '18 County Rd 419',
      city: 'Bastrop',
      county: 'Bastrop',
      state: 'TX',
      zip: '78602',
      typeOfStructure: 'Manufactured Home',
      residenceType: 'Primary',
      ownerOrRenter: 'Owner',
      insuranceStatus: 'No',
      insuranceCoverage: 'None',
      incidentType: 'Benefits Review',
      waterDepth: '',
      utilityOutages: '',
      utilityDamages: '',
      survivorDamageLevel: 'Major',
      generalComments: 'Veteran with unresolved VA disability claim. Previously denied; new documentation submitted. Needs employment assistance and transitional housing support.',
      regionDamageLevel: 'Major',
      regionStatus: 'Verified',
      regionComments: 'Upgraded priority after re-assessment.',
      latitude: '30.1105',
      longitude: '-97.3286',
      createdDate: '2026-06-11T14:30:00Z',
      potentialDup: 'Yes',
      parcelId: 'BA-2024-00418',
    },
  },
];

const INITIAL_ASSIGNMENT: AssignmentItem[] = [
  {
    id: 'asg-1',
    clientName: 'Henry Cole',
    caseId: 'CASE-4879-10590',
    county: 'Travis',
    tier: 'Tier 3',
    assignedTo: null,
  },
  {
    id: 'asg-2',
    clientName: 'Grace Lin',
    caseId: 'CASE-4879-10591',
    county: 'Williamson',
    tier: 'Tier 2',
    assignedTo: 'Marcus Lee',
  },
];

const INITIAL_REJECTED: RejectedItem[] = [
  {
    id: 'rej-1',
    clientName: 'Trevor Hunt',
    address: '12 Mesa Rd, Buda',
    county: 'Hays',
    damageLevel: 'Affected',
    incidentType: 'Initial Intake',
    dateReceived: '2026-06-01',
    source: 'iSTAT',
    reason: 'Duplicate of existing case CASE-4879-10500.',
    rejectedAt: '2026-06-02',
    rejectedBy: 'Jordan Reed',
  },
];

function searchMatch(haystack: string, q: string): boolean {
  return haystack.toLowerCase().includes(q.toLowerCase().trim());
}

function SearchBox({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <Box sx={{ p: (t) => t.spacing(2) }}>
      <TextField
        placeholder="Search…"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        size="small"
        fullWidth
        slotProps={{
          input: {
            startAdornment: (
              <Box
                sx={{
                  display: 'flex',
                  mr: 1,
                  color: (t) => t.palette.text.secondary,
                  '& svg': { width: 18, height: 18 },
                }}
              >
                <Magnify />
              </Box>
            ),
          },
        }}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Caseload Overview — per-caseworker stats
// ---------------------------------------------------------------------------

interface CaseloadRow {
  name: string;
  organization: string;
  openCases: number;
  closedCases: number;
  followUpsDue: number;
  followUpsOverdue: number;
  openGoals: number;
  openReferrals: number;
}

function CaseloadOverview() {
  const allCases = useMemo(() => mockGetCases(), []);
  const allFollowUps = useMemo(() => mockGetAllFollowUps(), []);
  const allGoals = useMemo(() => mockGetAllGoals(), []);
  const allReferrals = useMemo(() => mockGetAllReferrals(), []);
  const [expandedWorker, setExpandedWorker] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const today = new Date().toISOString().slice(0, 10);

  // Cases by supervisor for chart
  const supervisorData = useMemo(() => {
    const bySup = new Map<string, { open: number; closed: number }>();
    for (const c of allCases) {
      const sup = c.supervisor || 'Unassigned';
      const entry = bySup.get(sup) ?? { open: 0, closed: 0 };
      if (c.status === 'Open' || c.status === 'Pending' || c.status === 'Review') entry.open++;
      else if (c.status === 'Closed') entry.closed++;
      bySup.set(sup, entry);
    }
    return Array.from(bySup.entries())
      .map(([name, counts]) => ({ name, ...counts, total: counts.open + counts.closed }))
      .sort((a, b) => b.total - a.total);
  }, [allCases]);

  const maxCases = Math.max(...supervisorData.map((s) => s.total), 1);

  // Case manager rows
  const rows = useMemo<CaseloadRow[]>(() => {
    const byManager = new Map<string, TcrCase[]>();
    for (const c of allCases) {
      const mgr = c.assignedManager || 'Unassigned';
      const list = byManager.get(mgr) ?? [];
      list.push(c);
      byManager.set(mgr, list);
    }

    return Array.from(byManager.entries()).map(([name, cases]) => {
      const openCases = cases.filter((c) => c.status === 'Open' || c.status === 'Pending' || c.status === 'Review');
      const closedCases = cases.filter((c) => c.status === 'Closed');
      const caseIdSet = new Set(cases.map((c) => c.caseId));

      const workerFollowUps = allFollowUps.filter((f) => caseIdSet.has(f.caseId));
      const followUpsDue = workerFollowUps.filter((f) => f.nextFollowUpDate && f.nextFollowUpDate === today).length;
      const followUpsOverdue = workerFollowUps.filter((f) => f.nextFollowUpDate && f.nextFollowUpDate < today).length;

      const openGoals = allGoals.filter((g) => caseIdSet.has(g.caseId) && g.status === 'In Progress').length;
      const openReferrals = allReferrals.filter((r) => caseIdSet.has(r.caseId) && (r.status === 'Requested' || r.status === 'Referred')).length;

      return {
        name,
        organization: cases[0]?.managerOrganization ?? '—',
        openCases: openCases.length,
        closedCases: closedCases.length,
        followUpsDue,
        followUpsOverdue,
        openGoals,
        openReferrals,
      };
    }).sort((a, b) => b.openCases - a.openCases);
  }, [allCases, allFollowUps, allGoals, allReferrals, today]);

  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) => `${r.name} ${r.organization}`.toLowerCase().includes(q));
  }, [rows, search]);

  return (
    <Stack spacing={2}>
      {/* Cases by Supervisor — horizontal bar chart */}
      <SectionCard title="Cases by Supervisor">
        <Stack spacing={1.5}>
          {supervisorData.map((s) => (
            <Box key={s.name}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.name}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{s.open} open · {s.closed} closed</Typography>
              </Stack>
              <Stack direction="row" sx={{ height: 20, borderRadius: 1, overflow: 'hidden', bgcolor: 'grey.100' }}>
                <Box sx={{ width: `${(s.open / maxCases) * 100}%`, bgcolor: 'primary.main', transition: 'width 0.3s' }} />
                <Box sx={{ width: `${(s.closed / maxCases) * 100}%`, bgcolor: 'grey.400', transition: 'width 0.3s' }} />
              </Stack>
            </Box>
          ))}
          <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: 'primary.main' }} />
              <Typography variant="caption">Open</Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: 'grey.400' }} />
              <Typography variant="caption">Closed</Typography>
            </Stack>
          </Stack>
        </Stack>
      </SectionCard>

      {/* Case Manager caseload */}
      <SectionCard title={`Caseload by Case Manager (${filteredRows.length})`} disableContentPadding>
        <SearchBox value={search} onChange={setSearch} />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="center">Case Manager</TableCell>
                <TableCell align="center">Organization</TableCell>
                <TableCell align="center">Open</TableCell>
                <TableCell align="center">Closed</TableCell>
                <TableCell align="center">Due Today</TableCell>
                <TableCell align="center">Overdue</TableCell>
                <TableCell align="center">Open Goals</TableCell>
                <TableCell align="center">Open Referrals</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((r) => (
                <>
                  <TableRow
                    key={r.name}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setExpandedWorker(expandedWorker === r.name ? null : r.name)}
                  >
                    <TableCell align="center"><Typography variant="body2" sx={{ fontWeight: 600 }}>{r.name}</Typography></TableCell>
                    <TableCell align="center"><Typography variant="caption">{r.organization}</Typography></TableCell>
                    <TableCell align="center">
                      <Chip label={r.openCases} size="small" color={r.openCases > 5 ? 'warning' : 'default'} />
                    </TableCell>
                    <TableCell align="center">{r.closedCases}</TableCell>
                    <TableCell align="center">
                      {r.followUpsDue > 0 ? <Chip label={r.followUpsDue} size="small" color="info" /> : '0'}
                    </TableCell>
                    <TableCell align="center">
                      {r.followUpsOverdue > 0 ? <Chip label={r.followUpsOverdue} size="small" color="error" /> : '0'}
                    </TableCell>
                    <TableCell align="center">{r.openGoals}</TableCell>
                    <TableCell align="center">{r.openReferrals}</TableCell>
                  </TableRow>
                  {expandedWorker === r.name && (
                    <TableRow key={`${r.name}-detail`}>
                      <TableCell colSpan={8} sx={{ py: 0, bgcolor: 'action.hover' }}>
                        <TableContainer sx={{ maxHeight: 300 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell align="center">Case ID</TableCell>
                                <TableCell align="center">Client</TableCell>
                                <TableCell align="center">Event</TableCell>
                                <TableCell align="center">Tier</TableCell>
                                <TableCell align="center">Status</TableCell>
                                <TableCell align="center">Progression</TableCell>
                                <TableCell align="center">County</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {allCases.filter((c) => (c.assignedManager || 'Unassigned') === r.name).map((c) => (
                                <TableRow key={c.caseId} hover>
                                  <TableCell align="center"><Typography variant="body2" sx={{ fontWeight: 600 }}>{c.caseId}</Typography></TableCell>
                                  <TableCell align="center">{c.clientName}</TableCell>
                                  <TableCell align="center"><Typography variant="caption">{c.event}</Typography></TableCell>
                                  <TableCell align="center"><TierBadge tier={c.tier} /></TableCell>
                                  <TableCell align="center">
                                    <Chip label={c.status} size="small" variant="outlined" color={c.status === 'Open' ? 'success' : c.status === 'Closed' ? 'default' : 'warning'} />
                                  </TableCell>
                                  <TableCell align="center"><Typography variant="caption">{c.progression}</Typography></TableCell>
                                  <TableCell align="center"><Typography variant="caption">{c.county}</Typography></TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))}
              {filteredRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>No case managers match your search.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </SectionCard>
    </Stack>
  );
}

export function AdminPage() {
  const { role, availableEvents } = useVcm();
  const { navigateToPath } = useAppNavigation();
  const [tab, setTab] = useState<AdminTabId>('assignment');
  const [eventFilter, setEventFilter] = useState<string>('all');
  const canReopen = REOPEN_ROLES.includes(role);

  // --- Case Assignment state ---
  const [verification, setVerification] = useState<VerificationItem[]>(INITIAL_VERIFICATION);
  const [assignment, setAssignment] = useState<AssignmentItem[]>(INITIAL_ASSIGNMENT);
  const [rejected, setRejected] = useState<RejectedItem[]>(INITIAL_REJECTED);
  const [verifySearch, setVerifySearch] = useState('');
  const [assignSearch, setAssignSearch] = useState('');
  const [rejectedSearch, setRejectedSearch] = useState('');
  const [selectedAssignees, setSelectedAssignees] = useState<Record<string, string>>({});

  const [detailItem, setDetailItem] = useState<VerificationItem | null>(null);
  const [rejectTarget, setRejectTarget] = useState<VerificationItem | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  // --- Assessment Match Modal state ---
  type MatchStep = 'match' | 'case-decision' | 'comparison' | 'confirm';
  const [matchItem, setMatchItem] = useState<VerificationItem | null>(null);
  const [matchStep, setMatchStep] = useState<MatchStep>('match');
  const [matchResults, setMatchResults] = useState<IstatMatchResult[]>([]);
  const [selectedMatch, setSelectedMatch] = useState<IstatMatchResult | null>(null);
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  const [discrepancies, setDiscrepancies] = useState<IstatDiscrepancy[]>([]);
  const [linkMode, setLinkMode] = useState<'add-new-only' | 'notes-only'>('add-new-only');
  const [selectedEvent, setSelectedEvent] = useState<string>('');

  interface SuggestedWorkOrder { type: string; source: string; isDuplicate: boolean; selected: boolean; }
  const [existingWorkOrders, setExistingWorkOrders] = useState<Array<{ type: string; status: string }>>([]);
  const [suggestedWorkOrders, setSuggestedWorkOrders] = useState<SuggestedWorkOrder[]>([]);
  const [manualSearch, setManualSearch] = useState('');

  const allClients = useMemo(() => mockGetClients(), []);
  const allCasesForMatch = useMemo(() => mockGetCases(), []);

  const manualSearchResults = useMemo(() => {
    const q = manualSearch.toLowerCase().trim();
    if (q.length < 2) return [];
    return allClients.filter((c) =>
      `${c.firstName} ${c.lastName} ${c.id} ${c.phoneHome ?? ''} ${c.email ?? ''}`.toLowerCase().includes(q),
    ).slice(0, 10);
  }, [manualSearch, allClients]);

  const startVerify = (item: VerificationItem) => {
    const ir = item.istatRecord;
    if (!ir) {
      // No iSTAT record — skip matching, go straight to create
      finalizeVerify(item, null, null, []);
      return;
    }
    const results = matchIstatToClients(
      {
        firstName: ir.firstName,
        lastName: ir.lastName,
        phone: ir.phone,
        email: ir.email,
        address: ir.address,
        city: ir.city,
        county: ir.county,
        zip: ir.zip,
        damageLevel: ir.survivorDamageLevel,
        waterDepth: ir.waterDepth,
        ownerOrRenter: ir.ownerOrRenter,
        insuranceStatus: ir.insuranceStatus,
        parcelId: ir.parcelId ?? undefined,
      },
      allClients,
      allCasesForMatch,
    );
    setMatchItem(item);
    setMatchResults(results);
    setSelectedMatch(null);
    setSelectedCaseId(null);
    setDiscrepancies([]);
    setLinkMode('add-new-only');
    setManualSearch('');
    // Auto-detect event from iSTAT incident ID (e.g., "INC-2026-4879-0341" → "DR-4879-TX")
    const incidentNum = ir.incidentId.match(/INC-\d+-(\d+)/)?.[1] ?? '';
    const matchedEvent = availableEvents.find((e) => e.programCode.includes(incidentNum));
    setSelectedEvent(matchedEvent?.programCode ?? availableEvents[0]?.programCode ?? '');
    setMatchStep('match');
  };

  const selectManualClient = (client: typeof allClients[number]) => {
    const clientCases = allCasesForMatch
      .filter((c) => c.clientId === client.id)
      .map((c) => ({ caseId: c.caseId, event: c.program, status: c.status, tier: c.tier, damageLevel: c.housingCondition }));
    const manualResult: IstatMatchResult = {
      clientId: client.id,
      clientName: `${client.firstName} ${client.lastName}`,
      phone: client.phoneHome,
      address: client.primaryAddress?.address1 ?? null,
      county: client.county,
      score: 0,
      matchReasons: ['Manual override'],
      missedReasons: [],
      cases: clientCases,
    };
    selectMatchResult(manualResult);
  };

  const selectMatchResult = (result: IstatMatchResult) => {
    setSelectedMatch(result);
    if (result.cases.length > 0) {
      setMatchStep('case-decision');
    } else {
      // Client exists but no cases — go straight to confirm (new case)
      setSelectedCaseId(null);
      setMatchStep('confirm');
    }
  };

  const selectCaseForLink = (caseId: string) => {
    setSelectedCaseId(caseId);
    // Compare iSTAT fields against the case
    const tcrCase = allCasesForMatch.find((c) => c.caseId === caseId);
    const ir = matchItem?.istatRecord;
    if (tcrCase && ir) {
      const disc = compareIstatToCase(
        {
          firstName: ir.firstName,
          lastName: ir.lastName,
          phone: ir.phone,
          email: ir.email,
          address: ir.address,
          city: ir.city,
          county: ir.county,
          zip: ir.zip,
          damageLevel: ir.survivorDamageLevel,
          waterDepth: ir.waterDepth,
          ownerOrRenter: ir.ownerOrRenter,
          insuranceStatus: ir.insuranceStatus,
        },
        tcrCase,
      );
      setDiscrepancies(disc);
    }
    // Derive work orders from iSTAT data
    const existing = mockGetWorkOrdersByCaseId(caseId).map((wo) => ({ type: wo.workOrderType, status: wo.status }));
    setExistingWorkOrders(existing);
    const existingTypes = new Set(existing.map((wo) => wo.type));
    const suggested: SuggestedWorkOrder[] = [];
    if (ir) {
      // Derive service work orders from intake assessment type and comments
      const comments = (ir.generalComments ?? '').toLowerCase();
      const level = (ir.survivorDamageLevel ?? '').toLowerCase();
      const incidentType = (ir.incidentType ?? '').toLowerCase();
      if (incidentType.includes('housing') || comments.includes('housing') || comments.includes('homeless') || comments.includes('eviction')) {
        suggested.push({ type: 'Housing Assessment', source: 'Housing need detected', isDuplicate: existingTypes.has('Housing Assessment'), selected: !existingTypes.has('Housing Assessment') });
      }
      if (comments.includes('claim') || comments.includes('benefit') || comments.includes('va ') || comments.includes('ssvf')) {
        suggested.push({ type: 'Benefits Counseling', source: 'VA benefit need noted', isDuplicate: existingTypes.has('Benefits Counseling'), selected: !existingTypes.has('Benefits Counseling') });
      }
      if (comments.includes('employment') || comments.includes('job') || comments.includes('work')) {
        suggested.push({ type: 'Employment Assistance', source: 'Employment need noted', isDuplicate: existingTypes.has('Employment Assistance'), selected: !existingTypes.has('Employment Assistance') });
      }
      if (level === 'destroyed' || level === 'major') {
        suggested.push({ type: 'Healthcare Navigation', source: 'High-priority case — healthcare navigation recommended', isDuplicate: existingTypes.has('Healthcare Navigation'), selected: !existingTypes.has('Healthcare Navigation') });
      }
      if (comments.includes('transport') || comments.includes('commute')) {
        suggested.push({ type: 'Transportation Support', source: 'Transportation need noted', isDuplicate: existingTypes.has('Transportation Support'), selected: !existingTypes.has('Transportation Support') });
      }
    }
    setSuggestedWorkOrders(suggested);
    setMatchStep('comparison');
  };

  const createNewCaseForMatch = () => {
    setSelectedCaseId(null);
    setDiscrepancies([]);
    setMatchStep('confirm');
  };

  const finalizeVerify = (
    item: VerificationItem,
    _matchedClientId: string | null,
    _linkedCaseId: string | null,
    _discrepancies: IstatDiscrepancy[],
  ) => {
    setVerification((prev) => prev.filter((v) => v.id !== item.id));
    setAssignment((prev) => [
      ...prev,
      {
        id: `asg-${Date.now()}`,
        clientName: item.clientName,
        caseId: _linkedCaseId ?? `CASE-NEW-${prev.length + 1}`,
        county: item.county,
        tier: 'Tier 3',
        assignedTo: null,
      },
    ]);
    setMatchItem(null);
  };

  const confirmMatch = () => {
    if (!matchItem) return;
    finalizeVerify(
      matchItem,
      selectedMatch?.clientId ?? null,
      selectedCaseId,
      discrepancies,
    );
  };

  const confirmReject = () => {
    if (!rejectTarget) return;
    setVerification((prev) => prev.filter((v) => v.id !== rejectTarget.id));
    setRejected((prev) => [
      ...prev,
      {
        ...rejectTarget,
        reason: rejectReason || 'No reason provided.',
        rejectedAt: new Date().toISOString().slice(0, 10),
        rejectedBy: 'You',
      },
    ]);
    setRejectTarget(null);
    setRejectReason('');
  };

  const handleAssign = (item: AssignmentItem) => {
    const who = selectedAssignees[item.id] || item.assignedTo || CASEWORKERS[0] || 'Unassigned';
    setAssignment((prev) => prev.map((a) => (a.id === item.id ? { ...a, assignedTo: who } : a)));
  };

  const handleRestore = (item: RejectedItem) => {
    setRejected((prev) => prev.filter((r) => r.id !== item.id));
    const { reason: _reason, rejectedAt: _at, rejectedBy: _by, ...rest } = item;
    setVerification((prev) => [...prev, rest]);
  };

  const filteredVerification = useMemo(
    () =>
      verification.filter((v) =>
        searchMatch(`${v.clientName} ${v.address} ${v.damageLevel} ${v.incidentType}`, verifySearch),
      ),
    [verification, verifySearch],
  );
  const filteredAssignment = useMemo(
    () =>
      assignment.filter((a) =>
        searchMatch(`${a.clientName} ${a.caseId} ${a.county} ${a.tier} ${a.assignedTo ?? ''}`, assignSearch),
      ),
    [assignment, assignSearch],
  );
  const filteredRejected = useMemo(
    () =>
      rejected.filter((r) =>
        searchMatch(`${r.clientName} ${r.address} ${r.reason} ${r.damageLevel}`, rejectedSearch),
      ),
    [rejected, rejectedSearch],
  );

  // --- Event Management state ---
  const [events, setEvents] = useState<ManagedEvent[]>(() =>
    availableEvents.map((e) => ({
      name: e.name,
      programCode: e.programCode,
      startDate: '2026-05-01',
      counties: ['Travis', 'Williamson'],
      status: e.isActive ? 'Active' : 'Closed',
    })),
  );
  const [eventOpen, setEventOpen] = useState(false);
  const [eventDraft, setEventDraft] = useState<ManagedEvent>({
    name: '',
    programCode: '',
    startDate: '',
    counties: [],
    status: 'Active',
  });
  const [editingEvent, setEditingEvent] = useState<string | null>(null);

  const openCreateEvent = () => {
    setEditingEvent(null);
    setEventDraft({ name: '', programCode: '', startDate: '', counties: [], status: 'Active' });
    setEventOpen(true);
  };
  const openEditEvent = (ev: ManagedEvent) => {
    setEditingEvent(ev.programCode);
    setEventDraft({ ...ev, counties: [...ev.counties] });
    setEventOpen(true);
  };
  const saveEvent = () => {
    if (editingEvent) {
      setEvents((prev) => prev.map((e) => (e.programCode === editingEvent ? eventDraft : e)));
    } else {
      setEvents((prev) => [...prev, eventDraft]);
    }
    setEventOpen(false);
  };
  const toggleEventStatus = (programCode: string) =>
    setEvents((prev) =>
      prev.map((e) =>
        e.programCode === programCode
          ? { ...e, status: e.status === 'Active' ? 'Closed' : 'Active' }
          : e,
      ),
    );

  // --- Closed Cases state ---
  const [allCases, setAllCases] = useState<TcrCase[]>(() => mockGetCases());
  const [closedSearch, setClosedSearch] = useState('');
  const [reopenTarget, setReopenTarget] = useState<TcrCase | null>(null);

  const closedCases = useMemo(
    () =>
      allCases
        .filter((c) => c.status === 'Closed')
        .filter((c) => eventFilter === 'all' || c.programCode === eventFilter)
        .filter((c) =>
          searchMatch(
            `${c.caseId} ${c.clientName} ${c.county} ${c.event} ${c.assignedManager}`,
            closedSearch,
          ),
        ),
    [allCases, eventFilter, closedSearch],
  );

  const confirmReopen = () => {
    if (!reopenTarget) return;
    setAllCases((prev) =>
      prev.map((c) =>
        c.caseId === reopenTarget.caseId
          ? { ...c, status: 'Open', updatedAt: new Date().toISOString() }
          : c,
      ),
    );
    setReopenTarget(null);
  };

  // --- Audit Viewer state ---
  const audit = useMemo(() => mockGetAllAudit(), []);
  const [auditCase, setAuditCase] = useState('all');
  const [auditAction, setAuditAction] = useState('all');
  const [auditFrom, setAuditFrom] = useState('');
  const [auditTo, setAuditTo] = useState('');
  const uniqueCaseIds = useMemo(() => [...new Set(audit.map((a) => a.caseId))], [audit]);
  const uniqueActions = useMemo(() => [...new Set(audit.map((a) => a.action))], [audit]);
  const filteredAudit = useMemo(
    () =>
      audit
        .filter((a) => auditCase === 'all' || a.caseId === auditCase)
        .filter((a) => auditAction === 'all' || a.action === auditAction)
        .filter((a) => !auditFrom || a.timestamp.slice(0, 10) >= auditFrom)
        .filter((a) => !auditTo || a.timestamp.slice(0, 10) <= auditTo)
        .slice()
        .sort((x, y) => (x.timestamp < y.timestamp ? 1 : -1)),
    [audit, auditCase, auditAction, auditFrom, auditTo],
  );

  return (
    <Box
      sx={{
        p: (t) => t.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        gap: (t) => t.spacing(2.5),
      }}
    >
      <PageHeader
        title="Admin"
        description="Verify intake, assign cases, manage events, and review the audit log."
        actions={[
          <TextField
            key="event"
            select
            size="small"
            label="Event"
            value={eventFilter}
            onChange={(e) => setEventFilter(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="all">All events</MenuItem>
            {availableEvents.map((e) => (
              <MenuItem key={e.programCode} value={e.programCode}>
                {e.name}
              </MenuItem>
            ))}
          </TextField>,
        ]}
      />

      <Box sx={{ borderBottom: 1, borderColor: (t) => t.palette.divider }}>
        <Tabs
          value={tab}
          onChange={(_e, v: AdminTabId) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Admin tabs"
        >
          {ADMIN_TABS.map((t) => (
            <Tab key={t.id} value={t.id} label={t.label} />
          ))}
        </Tabs>
      </Box>

      {/* ---------------- Case Assignment ---------------- */}
      {/* ---------------- Caseload Manager ---------------- */}
      {tab === 'caseload' && <CaseloadOverview />}

      {/* ---------------- Case Assignment ---------------- */}
      {tab === 'assignment' && (
        <Stack spacing={2.5}>
          <SectionCard
            title={`Verification Queue (${filteredVerification.length})`}
            action={<Chip label="Intake Assessment" size="small" variant="outlined" />}
            disableContentPadding
          >
            <SearchBox value={verifySearch} onChange={setVerifySearch} />
            <TableContainer sx={{ maxHeight: 480 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">Client</TableCell>
                    <TableCell align="center">Address</TableCell>
                    <TableCell align="center">Assessment Level</TableCell>
                    <TableCell align="center">Assessment Type</TableCell>
                    <TableCell align="center">Date Received</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredVerification.map((v) => (
                    <TableRow key={v.id} hover>
                      <TableCell align="center">{v.clientName}</TableCell>
                      <TableCell align="center">{v.address}</TableCell>
                      <TableCell align="center">{v.damageLevel}</TableCell>
                      <TableCell align="center">{v.incidentType}</TableCell>
                      <TableCell align="center">{v.dateReceived}</TableCell>
                      <TableCell align="right" sx={{ width: 340 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {v.istatRecord && (
                            <Button size="small" variant="outlined" startIcon={<Eye />} sx={{ minWidth: 110 }} onClick={() => setDetailItem(v)}>
                              View Assessment
                            </Button>
                          )}
                          <Button size="small" variant="contained" sx={{ minWidth: 130 }} onClick={() => startVerify(v)}>
                            Verify &amp; Approve
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{ minWidth: 70 }}
                            onClick={() => {
                              setRejectTarget(v);
                              setRejectReason('');
                            }}
                          >
                            Reject
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredVerification.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: (t) => t.spacing(3) }}>
                        <Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary }}>
                          No items awaiting verification.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>

          <SectionCard title={`Assignment Queue (${filteredAssignment.length})`} disableContentPadding>
            <SearchBox value={assignSearch} onChange={setAssignSearch} />
            <TableContainer sx={{ maxHeight: 480 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">Client</TableCell>
                    <TableCell align="center">Case ID</TableCell>
                    <TableCell align="center">County</TableCell>
                    <TableCell align="center">Tier</TableCell>
                    <TableCell align="center">Assigned To</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAssignment.map((a) => (
                    <TableRow key={a.id} hover>
                      <TableCell align="center">{a.clientName}</TableCell>
                      <TableCell align="center">{a.caseId}</TableCell>
                      <TableCell align="center">{a.county}</TableCell>
                      <TableCell align="center">
                        <TierBadge tier={a.tier} />
                      </TableCell>
                      <TableCell align="center">
                        {a.assignedTo ? (
                          <Chip label={a.assignedTo} size="small" color="primary" variant="outlined" />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ width: 280 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <TextField
                            select
                            size="small"
                            value={selectedAssignees[a.id] ?? a.assignedTo ?? ''}
                            onChange={(e) =>
                              setSelectedAssignees((prev) => ({ ...prev, [a.id]: e.target.value }))
                            }
                            sx={{ width: 180 }}
                          >
                            <MenuItem value="">Select…</MenuItem>
                            {CASEWORKERS.map((w) => (
                              <MenuItem key={w} value={w}>
                                {w}
                              </MenuItem>
                            ))}
                          </TextField>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleAssign(a)}
                            sx={{ minWidth: 80, whiteSpace: 'nowrap' }}
                          >
                            {a.assignedTo ? 'Reassign' : 'Assign'}
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredAssignment.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: (t) => t.spacing(3) }}>
                        <Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary }}>
                          No cases awaiting assignment.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>
        </Stack>
      )}

      {/* ---------------- Event Management ---------------- */}
      {tab === 'events' && (
        <SectionCard
          title={`Events (${events.length})`}
          action={
            <Button size="small" variant="contained" onClick={openCreateEvent}>
              Create Event
            </Button>
          }
        >
          <Grid container spacing={2}>
            {events.map((ev) => (
              <Grid key={ev.programCode} size={{ xs: 12, md: 6 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle2">{ev.name}</Typography>
                      <Chip
                        label={ev.status}
                        size="small"
                        color={ev.status === 'Active' ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </Stack>
                    <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>
                      {ev.programCode} · Started {ev.startDate || '—'}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: (t) => t.spacing(1) }}>
                      Counties: {ev.counties.length > 0 ? ev.counties.join(', ') : '—'}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: (t) => t.spacing(1) }}>
                      <Button size="small" variant="outlined" onClick={() => openEditEvent(ev)}>
                        Edit
                      </Button>
                      <Button size="small" onClick={() => toggleEventStatus(ev.programCode)}>
                        {ev.status === 'Active' ? 'Close Event' : 'Reactivate'}
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </SectionCard>
      )}

      {/* ---------------- Rejected Cases ---------------- */}
      {tab === 'rejected' && (
        <SectionCard title={`Rejected Cases (${filteredRejected.length})`} disableContentPadding>
          <SearchBox value={rejectedSearch} onChange={setRejectedSearch} />
          <TableContainer sx={{ maxHeight: 480 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align="center">Client</TableCell>
                  <TableCell align="center">Address</TableCell>
                  <TableCell align="center">Assessment Level</TableCell>
                  <TableCell align="center">Assessment Type</TableCell>
                  <TableCell align="center">Rejection Reason</TableCell>
                  <TableCell align="center">Rejected</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRejected.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell align="center">{r.clientName}</TableCell>
                    <TableCell align="center">{r.address}</TableCell>
                    <TableCell align="center">{r.damageLevel}</TableCell>
                    <TableCell align="center">{r.incidentType}</TableCell>
                    <TableCell align="center">{r.reason}</TableCell>
                    <TableCell align="center">{new Date(r.rejectedAt).toLocaleDateString()}</TableCell>
                    <TableCell align="right" sx={{ width: 220 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Eye />}
                          sx={{ minWidth: 80 }}
                          onClick={() => setDetailItem(r)}
                        >
                          View
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ minWidth: 120 }}
                          onClick={() => handleRestore(r)}
                        >
                          Restore
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredRejected.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: (t) => t.spacing(3) }}>
                      <Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary }}>
                        No rejected cases.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionCard>
      )}

      {/* ---------------- Closed Cases ---------------- */}
      {tab === 'closed' && (
        <SectionCard title={`Closed Cases (${closedCases.length})`} disableContentPadding>
          {!canReopen && (
            <Box sx={{ px: (t) => t.spacing(2), pt: (t) => t.spacing(2) }}>
              <Chip label="Reopen restricted for your role" size="small" color="warning" />
            </Box>
          )}
          <SearchBox value={closedSearch} onChange={setClosedSearch} />
          <TableContainer sx={{ maxHeight: 480 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align="center">Case ID</TableCell>
                  <TableCell align="center">Client</TableCell>
                  <TableCell align="center">Event</TableCell>
                  <TableCell align="center">County</TableCell>
                  <TableCell align="center">Tier</TableCell>
                  <TableCell align="center">Case Manager</TableCell>
                  <TableCell align="center">Closure Reason</TableCell>
                  <TableCell align="center">Date Closed</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {closedCases.map((c) => (
                  <TableRow key={c.caseId} hover>
                    <TableCell align="center">{c.caseId}</TableCell>
                    <TableCell align="center">{c.clientName}</TableCell>
                    <TableCell align="center">{c.event}</TableCell>
                    <TableCell align="center">{c.county}</TableCell>
                    <TableCell align="center">
                      <TierBadge tier={c.tier} />
                    </TableCell>
                    <TableCell align="center">{c.assignedManager}</TableCell>
                    <TableCell align="center">{c.closureReason ?? '—'}</TableCell>
                    <TableCell align="center">{c.dateClosed ?? '—'}</TableCell>
                    <TableCell align="right" sx={{ width: 180 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ minWidth: 70 }}
                          onClick={() => navigateToPath(`cases/${c.caseId}`)}
                        >
                          View
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ minWidth: 80 }}
                          disabled={!canReopen}
                          onClick={() => setReopenTarget(c)}
                        >
                          Reopen
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {closedCases.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: (t) => t.spacing(3) }}>
                      <Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary }}>
                        No closed cases.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionCard>
      )}

      {/* ---------------- Audit Viewer ---------------- */}
      {tab === 'audit' && (
        <SectionCard title={`Audit Log (${filteredAudit.length})`} disableContentPadding>
          <Box sx={{ p: (t) => t.spacing(2) }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  select
                  size="small"
                  fullWidth
                  label="Case ID"
                  value={auditCase}
                  onChange={(e) => setAuditCase(e.target.value)}
                >
                  <MenuItem value="all">All cases</MenuItem>
                  {uniqueCaseIds.map((id) => (
                    <MenuItem key={id} value={id}>
                      {id}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  select
                  size="small"
                  fullWidth
                  label="Action"
                  value={auditAction}
                  onChange={(e) => setAuditAction(e.target.value)}
                >
                  <MenuItem value="all">All actions</MenuItem>
                  {uniqueActions.map((a) => (
                    <MenuItem key={a} value={a}>
                      {a}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  type="date"
                  size="small"
                  fullWidth
                  label="From"
                  value={auditFrom}
                  onChange={(e) => setAuditFrom(e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  type="date"
                  size="small"
                  fullWidth
                  label="To"
                  value={auditTo}
                  onChange={(e) => setAuditTo(e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Grid>
            </Grid>
          </Box>
          <TableContainer sx={{ maxHeight: 480 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align="center">Timestamp</TableCell>
                  <TableCell align="center">Case ID</TableCell>
                  <TableCell align="center">Action</TableCell>
                  <TableCell align="center">Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAudit.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{a.timestamp}</TableCell>
                    <TableCell align="center">{a.caseId}</TableCell>
                    <TableCell align="center">
                      {a.action}{' '}
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ color: (t) => t.palette.text.secondary }}
                      >
                        ({a.performer})
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{a.details ?? '—'}</TableCell>
                  </TableRow>
                ))}
                {filteredAudit.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: (t) => t.spacing(3) }}>
                      <Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary }}>
                        No audit entries match your filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionCard>
      )}

      {/* ---------------- Call Center ---------------- */}
      {tab === 'calls' && (() => {
        const allCalls = mockGetCallLog();
        const totalCalls = allCalls.length;
        const byReason = new Map<string, number>();
        const byOutcome = new Map<string, number>();
        const byHandler = new Map<string, number>();
        for (const c of allCalls) {
          byReason.set(c.reasonForCall, (byReason.get(c.reasonForCall) ?? 0) + 1);
          byOutcome.set(c.outcome, (byOutcome.get(c.outcome) ?? 0) + 1);
          byHandler.set(c.handledBy, (byHandler.get(c.handledBy) ?? 0) + 1);
        }
        return (
          <Stack spacing={2}>
            {/* Metrics */}
            <Grid container spacing={2}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Card variant="outlined"><CardContent sx={{ textAlign: 'center', py: 1.5, '&:last-child': { pb: 1.5 } }}><Typography variant="h5" sx={{ fontWeight: 700, color: (t) => t.palette.primary.main }}>{totalCalls}</Typography><Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>Total Calls</Typography></CardContent></Card>
              </Grid>
              {[...byOutcome.entries()].map(([outcome, count]) => (
                <Grid key={outcome} size={{ xs: 6, sm: 3 }}>
                  <Card variant="outlined"><CardContent sx={{ textAlign: 'center', py: 1.5, '&:last-child': { pb: 1.5 } }}><Typography variant="h5" sx={{ fontWeight: 700 }}>{count}</Typography><Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>{outcome}</Typography></CardContent></Card>
                </Grid>
              ))}
            </Grid>

            {/* By handler */}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <SectionCard title="Calls by Reason">
                  <Stack spacing={1}>
                    {[...byReason.entries()].sort((a, b) => b[1] - a[1]).map(([reason, count]) => (
                      <Stack key={reason} direction="row" justifyContent="space-between">
                        <Typography variant="body2">{reason}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{count}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </SectionCard>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <SectionCard title="Calls by Caseworker">
                  <Stack spacing={1}>
                    {[...byHandler.entries()].sort((a, b) => b[1] - a[1]).map(([handler, count]) => (
                      <Stack key={handler} direction="row" justifyContent="space-between">
                        <Typography variant="body2">{handler}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{count}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </SectionCard>
              </Grid>
            </Grid>

            {/* Full call log */}
            <SectionCard title={`All Calls (${totalCalls})`} disableContentPadding>
              <TableContainer sx={{ maxHeight: 480 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">Date</TableCell>
                      <TableCell align="center">Caller</TableCell>
                      <TableCell align="center">Phone</TableCell>
                      <TableCell align="center">Reason</TableCell>
                      <TableCell align="center">Handled By</TableCell>
                      <TableCell align="center">Outcome</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allCalls.sort((a, b) => b.createdAt.localeCompare(a.createdAt)).map((call) => (
                      <TableRow key={call.id} hover>
                        <TableCell align="center">{new Date(call.createdAt).toLocaleDateString()}</TableCell>
                        <TableCell align="center"><Typography variant="body2" sx={{ fontWeight: 600 }}>{call.callerName}</Typography></TableCell>
                        <TableCell align="center">{call.callerPhone}</TableCell>
                        <TableCell align="center">{call.reasonForCall}</TableCell>
                        <TableCell align="center">{call.handledBy}</TableCell>
                        <TableCell align="center"><Typography variant="body2" sx={{ fontWeight: 600 }}>{call.outcome}</Typography></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </SectionCard>
          </Stack>
        );
      })()}

      {/* Assessment detail dialog */}
      <Dialog open={detailItem !== null} onClose={() => setDetailItem(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          {detailItem?.istatRecord ? 'Intake Assessment Record' : 'Case Details'} — {detailItem?.clientName}
          <Typography variant="caption" sx={{ display: 'block', color: (t) => t.palette.text.secondary }}>
            {detailItem?.istatRecord ? `Assessment ${detailItem.istatRecord.incidentId} · ` : ''}Received {detailItem?.dateReceived ?? ''}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {/* Rejection details (shown for rejected items) */}
          {detailItem && 'reason' in detailItem && (
            <Box sx={{ mb: 2, p: 2, borderRadius: 1, border: 1, borderColor: 'error.light', bgcolor: 'error.lighter' }}>
              <SectionLabel>Rejection Details</SectionLabel>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 3 }}><DetailField label="Rejected By" value={(detailItem as RejectedItem).rejectedBy} /></Grid>
                <Grid size={{ xs: 6, sm: 3 }}><DetailField label="Date Rejected" value={new Date((detailItem as RejectedItem).rejectedAt).toLocaleDateString()} /></Grid>
              </Grid>
              <Box sx={{ mt: 1 }}>
                <Typography variant="overline" sx={{ fontSize: 10, color: (t) => t.palette.text.secondary }}>Reason</Typography>
                <Typography variant="body2">{(detailItem as RejectedItem).reason}</Typography>
              </Box>
            </Box>
          )}

          {/* Basic case info (shown when no assessment record) */}
          {detailItem && !detailItem.istatRecord && (
            <Stack spacing={2}>
              <Box>
                <SectionLabel>Submission Details</SectionLabel>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6, sm: 3 }}><DetailField label="Client Name" value={detailItem.clientName} /></Grid>
                  <Grid size={{ xs: 12, sm: 6 }}><DetailField label="Address" value={detailItem.address} /></Grid>
                  <Grid size={{ xs: 6, sm: 3 }}><DetailField label="County" value={detailItem.county} /></Grid>
                  <Grid size={{ xs: 6, sm: 3 }}><DetailField label="Damage Level" value={detailItem.damageLevel} highlight={detailItem.damageLevel === 'Major' || detailItem.damageLevel === 'Destroyed'} /></Grid>
                  <Grid size={{ xs: 6, sm: 3 }}><DetailField label="Incident Type" value={detailItem.incidentType} /></Grid>
                  <Grid size={{ xs: 6, sm: 3 }}><DetailField label="Date Received" value={detailItem.dateReceived} /></Grid>
                  <Grid size={{ xs: 6, sm: 3 }}><DetailField label="Source" value={detailItem.source} /></Grid>
                </Grid>
              </Box>
            </Stack>
          )}

          {/* Full assessment record */}
          {detailItem?.istatRecord && (() => {
            const r = detailItem.istatRecord;
            const mapQuery = [r.address, r.city, r.state, r.zip].filter(Boolean).join(', ');
            return (
              <Stack spacing={3}>
                {/* Map */}
                <Box sx={{ borderRadius: 2, overflow: 'hidden', border: 1, borderColor: 'divider' }}>
                  <iframe
                    title="Property location"
                    style={{ width: '100%', height: 200, border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    src={`https://maps.google.com/maps?q=${encodeURIComponent(mapQuery)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                    allowFullScreen
                  />
                  <Typography variant="caption" sx={{ display: 'block', px: 1.5, py: 1, bgcolor: 'action.hover', color: 'text.secondary' }}>
                    {mapQuery}
                  </Typography>
                </Box>

                {/* Client Information */}
                <Box>
                  <SectionLabel>Client Information</SectionLabel>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6, sm: 3 }}><DetailField label="First Name" value={r.firstName} /></Grid>
                    <Grid size={{ xs: 6, sm: 3 }}><DetailField label="Last Name" value={r.lastName} /></Grid>
                    <Grid size={{ xs: 6, sm: 3 }}><DetailField label="Phone" value={r.phone} /></Grid>
                    <Grid size={{ xs: 6, sm: 3 }}><DetailField label="Email" value={r.email} /></Grid>
                  </Grid>
                </Box>

                {/* Property */}
                <Box>
                  <SectionLabel>Property</SectionLabel>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}><DetailField label="Address" value={r.address} /></Grid>
                    <Grid size={{ xs: 6, sm: 3 }}><DetailField label="City" value={r.city} /></Grid>
                    <Grid size={{ xs: 6, sm: 3 }}><DetailField label="County" value={r.county} /></Grid>
                    <Grid size={{ xs: 6, sm: 3 }}><DetailField label="State" value={r.state} /></Grid>
                    <Grid size={{ xs: 6, sm: 3 }}><DetailField label="ZIP" value={r.zip} /></Grid>
                    <Grid size={{ xs: 6, sm: 3 }}><DetailField label="Structure Type" value={r.typeOfStructure} /></Grid>
                    <Grid size={{ xs: 6, sm: 3 }}><DetailField label="Residence Type" value={r.residenceType} /></Grid>
                    <Grid size={{ xs: 6, sm: 3 }}><DetailField label="Owner/Renter" value={r.ownerOrRenter} /></Grid>
                    <Grid size={{ xs: 6, sm: 3 }}><DetailField label="Property Type" value={r.propertyType} /></Grid>
                  </Grid>
                </Box>

                {/* Needs Assessment */}
                <Box>
                  <SectionLabel>Needs Assessment</SectionLabel>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6, sm: 3 }}><DetailField label="Housing Condition" value={r.survivorDamageLevel} highlight={r.survivorDamageLevel === 'Major' || r.survivorDamageLevel === 'Destroyed'} /></Grid>
                    <Grid size={{ xs: 6, sm: 3 }}><DetailField label="Assessment Type" value={r.incidentType} /></Grid>
                    <Grid size={{ xs: 6, sm: 3 }}><DetailField label="Assessment Date" value={r.incidentDate} /></Grid>
                  </Grid>
                </Box>

                {/* Insurance */}
                <Box>
                  <SectionLabel>Insurance</SectionLabel>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6, sm: 3 }}><DetailField label="Insurance Status" value={r.insuranceStatus} /></Grid>
                    <Grid size={{ xs: 6, sm: 3 }}><DetailField label="Coverage" value={r.insuranceCoverage} /></Grid>
                  </Grid>
                </Box>

                {/* Region Verification */}
                <Box>
                  <SectionLabel>Region Verification</SectionLabel>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6, sm: 3 }}><DetailField label="Region Damage Level" value={r.regionDamageLevel} /></Grid>
                    <Grid size={{ xs: 6, sm: 3 }}><DetailField label="Region Status" value={r.regionStatus} /></Grid>
                    <Grid size={{ xs: 12, sm: 6 }}><DetailField label="Region Comments" value={r.regionComments} /></Grid>
                  </Grid>
                </Box>

                {/* General Comments */}
                <Box>
                  <SectionLabel>General Comments</SectionLabel>
                  <Typography variant="body2" sx={{ bgcolor: 'action.hover', p: 1.5, borderRadius: 1, lineHeight: 1.7 }}>
                    {r.generalComments}
                  </Typography>
                </Box>

                {/* Record Info */}
                <Box>
                  <SectionLabel>Record Info</SectionLabel>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6, sm: 3 }}><DetailField label="Assessment ID" value={r.incidentId} /></Grid>
                    <Grid size={{ xs: 6, sm: 3 }}><DetailField label="Assessment Officer" value={r.fieldteamMember} /></Grid>
                    <Grid size={{ xs: 6, sm: 3 }}><DetailField label="Created" value={new Date(r.createdDate).toLocaleDateString()} /></Grid>
                    <Grid size={{ xs: 6, sm: 3 }}><DetailField label="Potential Duplicate" value={r.potentialDup} /></Grid>
                    <Grid size={{ xs: 6, sm: 3 }}><DetailField label="Latitude" value={r.latitude} /></Grid>
                    <Grid size={{ xs: 6, sm: 3 }}><DetailField label="Longitude" value={r.longitude} /></Grid>
                  </Grid>
                </Box>
              </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailItem(null)}>Close</Button>
          {detailItem && (
            <>
              <Button
                color="error"
                onClick={() => {
                  setRejectTarget(detailItem);
                  setRejectReason('');
                  setDetailItem(null);
                }}
              >
                Reject
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  startVerify(detailItem);
                  setDetailItem(null);
                }}
              >
                Verify &amp; Approve
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Reject reason dialog */}
      <Dialog open={rejectTarget !== null} onClose={() => setRejectTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reject intake</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Reason for rejection"
            fullWidth
            multiline
            minRows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmReject}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reopen confirm dialog */}
      <Dialog open={reopenTarget !== null} onClose={() => setReopenTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reopen case?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            This will set {reopenTarget?.caseId} back to Open and return it to active case management.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReopenTarget(null)}>Cancel</Button>
          <Button variant="contained" onClick={confirmReopen}>
            Reopen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Event create/edit dialog */}
      <Dialog open={eventOpen} onClose={() => setEventOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingEvent ? 'Edit event' : 'Create event'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Event Name"
              size="small"
              fullWidth
              value={eventDraft.name}
              onChange={(e) => setEventDraft((p) => ({ ...p, name: e.target.value }))}
            />
            <TextField
              label="Disaster Number"
              size="small"
              fullWidth
              value={eventDraft.programCode}
              onChange={(e) => setEventDraft((p) => ({ ...p, programCode: e.target.value }))}
            />
            <TextField
              label="Start Date"
              type="date"
              size="small"
              fullWidth
              value={eventDraft.startDate}
              onChange={(e) => setEventDraft((p) => ({ ...p, startDate: e.target.value }))}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              select
              label="Status"
              size="small"
              fullWidth
              value={eventDraft.status}
              onChange={(e) =>
                setEventDraft((p) => ({ ...p, status: e.target.value as ManagedEvent['status'] }))
              }
            >
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Closed">Closed</MenuItem>
            </TextField>
            <Autocomplete
              multiple
              size="small"
              options={[...TEXAS_COUNTIES]}
              value={eventDraft.counties}
              onChange={(_, next) => setEventDraft((p) => ({ ...p, counties: next }))}
              disableCloseOnSelect
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option}>
                  <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
                  {option}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Affected Counties" placeholder="Select counties..." />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...rest } = getTagProps({ index });
                  return <Chip key={key} label={option} size="small" {...rest} />;
                })
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEventOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveEvent}
            disabled={!eventDraft.name.trim() || !eventDraft.programCode.trim()}
          >
            {editingEvent ? 'Save changes' : 'Create event'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Assessment Match Modal ── */}
      <Dialog open={matchItem !== null} onClose={() => setMatchItem(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          {matchStep === 'match' && 'Step 1: Match to Existing Client'}
          {matchStep === 'case-decision' && 'Step 2: Select Case'}
          {matchStep === 'comparison' && 'Step 3: Review Differences'}
          {matchStep === 'confirm' && 'Step 4: Confirm'}
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
            {matchItem?.clientName} · {matchItem?.address}
            {matchItem?.istatRecord && ` · Assessment ${matchItem.istatRecord.incidentId}`}
            {matchItem?.istatRecord?.parcelId && ` · Application: ${matchItem.istatRecord.parcelId}`}
          </Typography>
          {selectedEvent && (
            <Chip
              label={`Event: ${availableEvents.find((e) => e.programCode === selectedEvent)?.name ?? selectedEvent} (${selectedEvent})`}
              size="small"
              color="primary"
              sx={{ mt: 0.5 }}
            />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {/* Step 1: Match results */}
          {matchStep === 'match' && (
            <Stack spacing={2}>
              {matchResults.length > 0 ? (
                <>
                  <Alert severity="info">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {matchResults.length} potential match{matchResults.length !== 1 ? 'es' : ''} found
                    </Typography>
                  </Alert>
                  {matchResults.map((result) => (
                    <Card key={result.clientId} variant="outlined" sx={{ cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }} onClick={() => selectMatchResult(result)}>
                      <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{result.clientName}</Typography>
                          <Chip label={`${result.score}% match`} size="small" color={result.score >= 50 ? 'success' : 'warning'} sx={{ flexShrink: 0 }} />
                        </Stack>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                          {result.phone ?? 'No phone'} · {result.address ?? 'No address'} · {result.county} County
                        </Typography>
                        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                          {result.matchReasons.includes('Application ID matches') && (
                            <Chip key="parcel" label="Application ID matches" size="small" color="success" variant="outlined" />
                          )}
                          {result.matchReasons.filter((r) => r !== 'Application ID matches').map((r) => (
                            <Chip key={r} label={r} size="small" color="success" variant="outlined" />
                          ))}
                        </Stack>
                        {result.missedReasons.length > 0 && (
                          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                            {result.missedReasons.map((r) => (
                              <Chip key={r} label={r} size="small" variant="outlined" sx={{ color: 'text.disabled', borderColor: 'divider', fontSize: 11 }} />
                            ))}
                          </Stack>
                        )}
                        {result.cases.length > 0 && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                            {result.cases.length} existing case{result.cases.length !== 1 ? 's' : ''}: {result.cases.map((c) => c.caseId).join(', ')}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </>
              ) : (
                <Alert severity="warning">
                  No existing clients match this assessment record.
                </Alert>
              )}

              {/* Manual search override */}
              <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1 }}>
                  Manual search — override auto-match
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search by name, client ID, phone, or email..."
                  value={manualSearch}
                  onChange={(e) => setManualSearch(e.target.value)}
                />
                {manualSearchResults.length > 0 && (
                  <Stack spacing={0.5} sx={{ mt: 1 }}>
                    {manualSearchResults.map((client) => (
                      <Stack key={client.id} direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1, py: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{client.firstName} {client.lastName}</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {client.id} · {client.phoneHome ?? 'No phone'} · {client.county} County
                          </Typography>
                        </Box>
                        <Button size="small" variant="outlined" onClick={() => selectManualClient(client)}>
                          Select
                        </Button>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          )}

          {/* Step 2: Case decision */}
          {matchStep === 'case-decision' && selectedMatch && (() => {
            // Detect which events already have cases
            const coveredEvents = new Set(selectedMatch.cases.map((c) => c.event));
            const allEventsCovered = availableEvents.every((e) => coveredEvents.has(e.name));
            return (
              <Stack spacing={2}>
                <Typography variant="body2">
                  <b>{selectedMatch.clientName}</b> has {selectedMatch.cases.length} existing case{selectedMatch.cases.length !== 1 ? 's' : ''}. Link the assessment to an existing case or create a new one for a different program.
                </Typography>
                {selectedMatch.cases.map((c) => (
                  <Card key={c.caseId} variant="outlined" sx={{ cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }} onClick={() => selectCaseForLink(c.caseId)}>
                    <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{c.caseId}</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{c.event} · {c.status}</Typography>
                        </Box>
                        <Stack direction="row" spacing={0.5}>
                          <TierBadge tier={c.tier} />
                          <Chip label={c.damageLevel ?? 'Not assessed'} size="small" variant="outlined" />
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
                {allEventsCovered ? (
                  <Alert severity="info">
                    This client already has a case for every active event. You can only link to an existing case.
                  </Alert>
                ) : (
                  <>
                    <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1 }}>
                      Or create a new case for an event this client doesn't have yet:
                    </Typography>
                    <TextField
                      select
                      size="small"
                      label="Event"
                      value={selectedEvent}
                      onChange={(e) => setSelectedEvent(e.target.value)}
                      fullWidth
                    >
                      <MenuItem value="">Select event...</MenuItem>
                      {availableEvents
                        .filter((e) => !coveredEvents.has(e.name))
                        .map((e) => (
                          <MenuItem key={e.programCode} value={e.programCode}>
                            {e.name} ({e.programCode})
                          </MenuItem>
                        ))}
                    </TextField>
                    <Button variant="outlined" onClick={createNewCaseForMatch} disabled={!selectedEvent}>
                      Create new case for this event
                    </Button>
                  </>
                )}
              </Stack>
            );
          })()}

          {/* Step 3: Comparison */}
          {matchStep === 'comparison' && (
            <Stack spacing={2}>
              <Typography variant="body2">
                Comparing assessment data against <b>{selectedCaseId}</b>. Fields that differ are flagged for caseworker review.
              </Typography>
              {discrepancies.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell align="center">Field</TableCell>
                        <TableCell align="center">Case Record</TableCell>
                        <TableCell align="center">Intake Assessment</TableCell>
                        <TableCell align="center">Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {discrepancies.map((d) => (
                        <TableRow key={d.field}>
                          <TableCell align="center"><Typography variant="body2" sx={{ fontWeight: 600 }}>{d.label}</Typography></TableCell>
                          <TableCell align="center">{d.caseValue}</TableCell>
                          <TableCell align="center"><Typography variant="body2" sx={{ fontWeight: 600, color: 'warning.main' }}>{d.istatValue}</Typography></TableCell>
                          <TableCell align="center"><Chip label="Differs" size="small" color="warning" variant="outlined" /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="success">All fields match — no discrepancies found.</Alert>
              )}
              {/* Work Orders */}
              {existingWorkOrders.length > 0 && (
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>Existing Work Orders on Case</Typography>
                  <Stack spacing={0.5}>
                    {existingWorkOrders.map((wo) => (
                      <Typography key={wo.type} variant="caption">
                        {wo.type} — <Chip label={wo.status} size="small" variant="outlined" sx={{ height: 18, fontSize: 11 }} />
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              )}
              {suggestedWorkOrders.length > 0 && (
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>Suggested Work Orders from Assessment</Typography>
                  <Stack spacing={0.5}>
                    {suggestedWorkOrders.map((wo, idx) => (
                      <Stack key={wo.type} direction="row" alignItems="center" spacing={1}>
                        <Checkbox
                          size="small"
                          checked={wo.selected}
                          disabled={wo.isDuplicate}
                          onChange={() => setSuggestedWorkOrders((prev) => prev.map((w, i) => i === idx ? { ...w, selected: !w.selected } : w))}
                        />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>{wo.type}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>— {wo.source}</Typography>
                        {wo.isDuplicate && <Chip label="Already exists" size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: 11 }} />}
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              )}
              {suggestedWorkOrders.length === 0 && linkMode === 'add-new-only' && (
                <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  No new work orders detected from this assessment.
                </Typography>
              )}

              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>How should we handle this data?</Typography>
              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                <Button
                  variant={linkMode === 'add-new-only' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setLinkMode('add-new-only')}
                >
                  Add to notes + create work orders
                </Button>
                <Button
                  variant={linkMode === 'notes-only' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setLinkMode('notes-only')}
                >
                  Add to case notes only
                </Button>
              </Stack>
              {linkMode === 'add-new-only' && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Intake assessment summary will be added to case notes. Selected work orders will be created. No existing case fields will be changed. Discrepancies will be flagged for the caseworker to review.
                </Typography>
              )}
              {linkMode === 'notes-only' && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  No fields will be changed or flagged. A summary of the intake assessment will be added to the case notes for reference.
                </Typography>
              )}
            </Stack>
          )}

          {/* Step 4: Confirm */}
          {matchStep === 'confirm' && (() => {
            const eventName = availableEvents.find((e) => e.programCode === selectedEvent)?.name;
            return (
              <Stack spacing={2}>
                <Alert severity="info">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Summary</Typography>
                  {selectedMatch && selectedCaseId && (
                    <Typography variant="body2">
                      Link assessment to <b>{selectedMatch.clientName}</b> → <b>{selectedCaseId}</b>
                      {linkMode === 'notes-only' && ' — assessment summary added to case notes only'}
                      {linkMode === 'add-new-only' && (() => {
                        const woCount = suggestedWorkOrders.filter((w) => w.selected).length;
                        const parts: string[] = [];
                        if (woCount > 0) parts.push(`${woCount} work order${woCount !== 1 ? 's' : ''} added`);
                        if (discrepancies.length > 0) parts.push(`${discrepancies.length} discrepanc${discrepancies.length === 1 ? 'y' : 'ies'} flagged`);
                        return parts.length > 0 ? ` — ${parts.join(', ')}` : '';
                      })()}
                    </Typography>
                  )}
                  {selectedMatch && !selectedCaseId && (
                    <Typography variant="body2">
                      Create new case for <b>{selectedMatch.clientName}</b> under <b>{eventName ?? selectedEvent}</b>
                    </Typography>
                  )}
                  {!selectedMatch && (
                    <Typography variant="body2">
                      Create new client <b>{matchItem?.clientName}</b> + new case under <b>{eventName ?? selectedEvent}</b>
                    </Typography>
                  )}
                </Alert>
                {/* Event selector for new cases */}
                {!selectedCaseId && (
                  <TextField
                    select
                    size="small"
                    label="Assign to Event"
                    value={selectedEvent}
                    onChange={(e) => setSelectedEvent(e.target.value)}
                    fullWidth
                  >
                    <MenuItem value="">Select event...</MenuItem>
                    {availableEvents.map((e) => (
                      <MenuItem key={e.programCode} value={e.programCode}>
                        {e.name} ({e.programCode})
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions>
          {matchStep === 'match' && (
            <>
              <Button onClick={() => setMatchItem(null)}>Cancel</Button>
              <Button variant="contained" onClick={() => { setSelectedMatch(null); setMatchStep('confirm'); }}>
                No match — create new client
              </Button>
            </>
          )}
          {matchStep === 'case-decision' && (
            <Button onClick={() => setMatchStep('match')}>Back</Button>
          )}
          {matchStep === 'comparison' && (
            <>
              <Button onClick={() => setMatchStep('case-decision')}>Back</Button>
              <Button variant="contained" onClick={() => setMatchStep('confirm')}>Continue</Button>
            </>
          )}
          {matchStep === 'confirm' && (
            <>
              <Button onClick={() => setMatchStep(selectedMatch ? (selectedCaseId ? 'comparison' : 'case-decision') : 'match')}>Back</Button>
              <Button variant="contained" onClick={confirmMatch}>Confirm</Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function SectionLabel({ children }: { readonly children: React.ReactNode }) {
  return (
    <Typography
      variant="overline"
      sx={{ display: 'block', mb: 1, fontWeight: 700, color: (t) => t.palette.text.secondary }}
    >
      {children}
    </Typography>
  );
}

function DetailField({
  label,
  value,
  highlight,
}: {
  readonly label: string;
  readonly value: string;
  readonly highlight?: boolean;
}) {
  return (
    <Box>
      <Typography variant="overline" sx={{ fontSize: 10, color: (t) => t.palette.text.secondary }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: 500, color: highlight ? 'error.main' : 'text.primary' }}
      >
        {value || '\u2014'}
      </Typography>
    </Box>
  );
}
